/**
 * The single place the station-volumes UI formats a volume or a station
 * (rulings G9, G10, T10.2).
 *
 * It lives in the lib rather than in `StationVolumeDock.tsx` so the dock's
 * children (chart, table, cross-section) can import it without importing
 * their own parent back — a circular import that would otherwise be baked
 * into every one of them.
 */
import { formatStation } from '@/core/alignment';

import type { StationBin } from './types';

/**
 * U+2212 MINUS SIGN. Every figure this module prints uses it (ruling A15):
 * an ASCII hyphen is a different glyph at a different height and width, and
 * in a column of `tabular-nums` it does not line up with the digits above it.
 *
 * The CSV is deliberately NOT part of this — `stationExport.ts`'s data rows
 * stay ASCII so Excel, pandas and QGIS parse them as numbers.
 */
const MINUS = '−';

/** Fixed precision for detailed Station volume station, volume and Z values. */
export const STATION_VOLUME_DISPLAY_DECIMALS = 3;

/**
 * Negative zero, normalised away once, here — `(-0).toFixed(2)` is `-0.00`,
 * and a client reading `−0.00 m²` in an areas column reasonably asks which
 * direction the material went. Values that round to all zeros lose the sign
 * for the same reason: the sign of a printed zero carries no information the
 * reader can use.
 */
const z = (v: number) => (v === 0 ? 0 : v);

/** `toFixed`, with the house minus and no signed zeros. The one place a
 *  volume or an area becomes a string. */
function fixed(v: number, digits: number): string {
  const s = z(v).toFixed(digits);
  if (!s.startsWith('-')) return s;
  // `-0`, `-0.00`, `-0.000` — a sign in front of nothing.
  return /[1-9]/.test(s) ? `${MINUS}${s.slice(1)}` : s.slice(1);
}

/**
 * The magnitude rule (G9, extended 2026-08-28): 3 decimals below 100 m³, 1 at
 * or above, and none at all from 10 000 m³ — a corridor net of +30 000 m³ is
 * not known to the litre, and the trailing digits only hide the figure.
 */
const digitsFor = (peak: number) => (peak >= 10_000 ? 0 : peak >= 100 ? 1 : 3);

const peakOf = (values: readonly number[]) => values.reduce((m, v) => Math.max(m, Math.abs(v)), 0);

/**
 * Cubic metres for a single figure: 3 decimals below 100 m³, 1 decimal at or
 * above, no thousands separator. This is the surface-difference panel's
 * long-standing rule, with the threshold taken on the **magnitude** rather
 * than the signed value — the original `v >= 100` gave a −5 000 m³ net
 * three decimals of entirely spurious precision. `SurfaceDifferencePanel`
 * imports this, so the two panels cannot disagree about decimals.
 *
 * (Not the spec's "2 dp below 1000" parenthetical, which never matched the
 * repo — ruling G9, as amended in fix round 1.)
 */
export function formatM3(v: number): string {
  return fixed(v, digitsFor(Math.abs(v)));
}

/**
 * Cubic metres for a **row** of related figures — a Cut / Fill / Net KPI row,
 * a tooltip's value column, a table row's totals.
 *
 * By default, precision is chosen once from the largest magnitude in the row
 * and applied to every member. Detailed views can pass `decimals` to pin the
 * precision explicitly. Per-figure precision makes a row read as
 * `Cut 310.0 · Fill 95.000 · Net −215.000`, where the trailing zeros look
 * like a claim about accuracy that the coarse figure beside them contradicts.
 * One precision per row is the honest presentation.
 */
export function formatM3Row(values: number[], decimals?: number): string[] {
  if (values.length === 0) return [];
  const digits = decimals ?? digitsFor(peakOf(values));
  return values.map((v) => fixed(v, digits));
}

/**
 * Ticks for a colour KEY — the ±peak labels under the 3D bands' gradient.
 *
 * Deliberately coarser than `formatM3Row`, and deliberately NOT sharing its
 * ladder. Those figures are measurements and their precision is ruling G9,
 * shared with `SurfaceDifferencePanel`; these are axis labels on a 96 px
 * gradient, where the row read `−26.273  0.000  +26.273` — three numbers
 * claiming litre precision for a colour ramp, in a space that fits about
 * fifteen characters. A key says roughly how far the colour goes.
 *
 * One precision for the row, from the peak, exactly as `formatM3Row` does:
 * per-figure precision would print `−26  0.000  +26`.
 */
export function formatM3Legend(values: number[], decimals?: number): string[] {
  if (values.length === 0) return [];
  const peak = peakOf(values);
  const digits = decimals ?? (peak >= 10 ? 0 : peak >= 1 ? 1 : 2);
  return values.map((v) => fixed(v, digits));
}

/** `12.345 m³` — the unit spelled out, for single readouts. */
export function formatM3WithUnit(v: number | null | undefined): string {
  return v == null ? '—' : `${formatM3(v)} m³`;
}

/**
 * Any other figure in this feature at a fixed number of decimals — an
 * elevation, a Δz, an offset. Goes through the same `fixed` as the volumes,
 * so every number in the dock carries the same minus and no signed zeros.
 */
export function formatFixed(v: number, decimals: number): string {
  return fixed(v, decimals);
}

/**
 * A figure whose SIGN is the point: a crossfall, and the difference between a
 * measured crossfall and the design's. `−2.61` falls left, `+2.61` falls
 * right, and a bare `2.61` in a column that holds both is unreadable — so a
 * positive value carries an explicit `+`.
 *
 * The same `fixed` as every other figure in this dock, so the minus is the
 * house U+2212 and a value that rounds to all zeros loses its sign rather
 * than printing `+0.00` / `−0.00` for the same measurement.
 */
export function formatSigned(v: number, decimals: number): string {
  const s = fixed(v, decimals);
  return /^[0-9]/.test(s) && /[1-9]/.test(s) ? `+${s}` : s;
}

/**
 * Square metres — cross-sectional end areas, corridor areas. The same
 * magnitude rule as the volumes, one step coarser (2 decimals below 100 m²,
 * 1 at or above, none from 10 000 m²), with the same minus and the same
 * negative-zero handling, so an area and a volume printed side by side read
 * as one family of figures. Pass `decimals` to pin a precision explicitly.
 */
export function formatM2(v: number, decimals?: number): string {
  const abs = Math.abs(v);
  return fixed(v, decimals ?? (abs >= 10_000 ? 0 : abs >= 100 ? 1 : 2));
}

/**
 * A coverage percentage. Whole percent everywhere in this feature (ruling
 * A15) — a bin that covers 97.4 % of its corridor is not measured to a tenth
 * of a percent, and the extra digit only makes the column harder to scan.
 *
 * The unit is deliberately NOT part of the string: `%` is stated once, in the
 * column header, the KPI's unit slot or the surrounding sentence, never on
 * every cell.
 *
 * It exists as its own function rather than as `formatFixed(v, 0)` at six call
 * sites so that the dock's KPI, the table cell, the chart tooltip, the chart's
 * hidden table and the cross-section chip cannot drift apart on decimals — the
 * same reason `formatM3` exists.
 */
export function formatPct(v: number): string {
  return fixed(v, 0);
}

/**
 * The per-bin fields a dataset-wide formatter pools over. The AEA pair is
 * optional: the chart never draws it, the table hides it by default, and the
 * super-bins the chart groups do not carry it.
 */
export type M3Poolable = Pick<StationBin, 'cutVolumeM3' | 'fillVolumeM3' | 'netVolumeM3' | 'cumulativeNetM3'> &
  Partial<Pick<StationBin, 'cutVolumeAeaM3' | 'fillVolumeAeaM3'>>;

/**
 * One precision for a whole VIEW, not for one row (ruling A15).
 *
 * `formatM3Row` fixes the decimals of the figures it is handed, which is
 * right for a KPI row but wrong for a view: the chart tooltip on a 10 m³ bin
 * printed `10.830` while the table below it printed `10.8` for the same bin,
 * because the row the tooltip pooled over was three small numbers and the
 * table pooled over the whole corridor. Same bin, two precisions, no way for
 * the reader to know which is the real figure.
 *
 * So by default the precision is chosen ONCE from every per-bin volume in the dataset —
 * cut, fill, net and the AEA cross-check, which is the same quantity by a
 * different method and is read straight against them — and used everywhere
 * those figures appear: chart tooltip, chart screen-reader table, table body,
 * 3D tooltip and the PDF. `cumulative` is a running total an order of
 * magnitude larger than the bins it sums, so it gets its own precision rather
 * than dragging every bin figure down to 1 dp. Detailed views can pass
 * `decimals` to pin both formatters to an explicit precision.
 */
export interface DatasetM3Formatters {
  /** Cut, fill, net and AEA — one precision pooled across every bin. */
  volume: (v: number) => string;
  /** Cumulative net — its own precision, for the same reason. */
  cumulative: (v: number) => string;
}

export function m3Formatter(bins: readonly M3Poolable[], decimals?: number): DatasetM3Formatters {
  let peak = 0;
  let cumPeak = 0;
  for (const b of bins) {
    peak = Math.max(peak, Math.abs(b.cutVolumeM3), Math.abs(b.fillVolumeM3), Math.abs(b.netVolumeM3));
    if (b.cutVolumeAeaM3 != null) peak = Math.max(peak, Math.abs(b.cutVolumeAeaM3));
    if (b.fillVolumeAeaM3 != null) peak = Math.max(peak, Math.abs(b.fillVolumeAeaM3));
    cumPeak = Math.max(cumPeak, Math.abs(b.cumulativeNetM3));
  }
  const digits = decimals ?? digitsFor(peak);
  const cumDigits = decimals ?? digitsFor(cumPeak);
  return {
    volume: (v: number) => fixed(v, digits),
    cumulative: (v: number) => fixed(v, cumDigits),
  };
}

/**
 * A display station without the `STA` tag — `1+200.000`. Used wherever the
 * context already says "station" (axis ticks, table cells, ranges), so the
 * tag would be three characters of noise on every row.
 */
export function staOnly(display: number, decimals = STATION_VOLUME_DISPLAY_DECIMALS): string {
  const tagged = formatStation(display, decimals);
  return tagged.startsWith('-') ? `-${tagged.slice('-STA '.length)}` : tagged.slice('STA '.length);
}

/** A display station with the `STA` tag — `STA 1+200.000`. */
export function staTag(display: number, decimals = STATION_VOLUME_DISPLAY_DECIMALS): string {
  return formatStation(display, decimals);
}

/** `1+200.000 – 1+220.000` (en dash), for a bin's display extent. */
export function formatStationRange(
  bin: Pick<StationBin, 'staStartDisplayM' | 'staEndDisplayM'>,
  decimals = STATION_VOLUME_DISPLAY_DECIMALS,
): string {
  return `${staOnly(bin.staStartDisplayM, decimals)} – ${staOnly(bin.staEndDisplayM, decimals)}`;
}
